/* =============================================================
 * Luxanium Product Customizer
 * - Three.js 360° metal-sheet mockup (front/back panels via schema)
 * - Real-time uploaded-photo preview composited onto the sheet
 * - Crop / zoom / rotate editor (matches competitor frames 18-20)
 * - Cloudinary unsigned upload (PNG / JPG, max 20MB)
 * - Buy It Now -> AJAX add to cart (image URL as line item property) -> /checkout
 *
 * Requires THREE (r128) to be loaded before this file.
 * ============================================================= */
(function () {
  'use strict';

  var MAX_BYTES = 20 * 1024 * 1024; // 20MB
  var ACCEPTED = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/avif'];
  var HEIC_RE = /\.(heic|heif)$/i;
  var TEX_W = 2048; // composited texture width — high enough to stay crisp
  var SIZE_LETTERS = ['S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL', '5XL'];

  /* Parse "12 × 18 Inch" / "18x12" -> {w:12,h:18,orientation,ratio} */
  function parseSize(title) {
    var m = (title || '').match(/(\d+(?:\.\d+)?)\s*[×xX]\s*(\d+(?:\.\d+)?)/);
    if (!m) return { w: 2, h: 3, orientation: 'portrait', ratio: 2 / 3 };
    var w = parseFloat(m[1]);
    var h = parseFloat(m[2]);
    return {
      w: w,
      h: h,
      ratio: w / h,
      orientation: w >= h ? 'landscape' : 'portrait'
    };
  }

  /* "12 x 18 in"  /  "30 x 46 cm"  label for a size */
  function dimLabel(size, unit) {
    if (unit === 'cm') {
      var cw = Math.round(size.w * 2.54);
      var ch = Math.round(size.h * 2.54);
      return cw + ' x ' + ch + ' cm';
    }
    return size.w + ' x ' + size.h + ' in';
  }

  function clamp(v, min, max) { return Math.min(max, Math.max(min, v)); }

  class LuxProductCustomizer extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.cfg = {
        cloudName: this.dataset.cloudName,
        uploadPreset: this.dataset.uploadPreset,
        dpi: parseInt(this.dataset.dpi, 10) || 100,
        productType: this.dataset.productType || 'Wall',
        panels: {
          portrait: {
            front: this.dataset.panelFrontPortrait,
            back: this.dataset.panelBackPortrait
          },
          landscape: {
            front: this.dataset.panelFrontLandscape,
            back: this.dataset.panelBackLandscape
          }
        }
      };

      try {
        this.variants = JSON.parse(
          this.querySelector('[data-lux-variants]').textContent
        );
      } catch (e) { this.variants = []; }

      this.unit = 'in'; // in | cm  (cm is just a label conversion)

      // shared photo transform used by BOTH the 3D texture and the crop editor
      this.transform = { scale: 1, x: 0, y: 0, rot: 0 };
      this.userImage = null;        // HTMLImageElement of the uploaded photo
      this.sourceUrl = null;        // Cloudinary URL of the original upload
      this.uploading = false;

      // pick the first available variant as the default selection
      this.currentVariant =
        this.variants.find(function (v) { return v.available; }) ||
        this.variants[0];
      this.currentSize = parseSize(this.currentVariant && this.currentVariant.title);

      this.cacheDom();
      this.bindEvents();
      this.initThree();
      this.renderSizeButtons();
      this.selectVariant(this.currentVariant && this.currentVariant.id, true);
    }

    cacheDom() {
      this.stage      = this.querySelector('[data-lux-stage]');
      this.fileInput  = this.querySelector('[data-lux-file]');
      this.uploadBtn  = this.querySelector('[data-lux-upload]');
      this.editBtn    = this.querySelector('[data-lux-edit]');
      this.buyBtn     = this.querySelector('[data-lux-buy]');
      this.atcBtn     = this.querySelector('[data-lux-atc]');
      if (this.buyBtn) this.buyBtn.disabled = true;
      if (this.atcBtn) this.atcBtn.disabled = true;
      this.metaEl     = this.querySelector('[data-lux-meta]');
      this.sizeWrap   = this.querySelector('[data-lux-sizes]');
      this.sizeLabel  = this.querySelector('[data-lux-size-label]');
      this.hint       = this.querySelector('[data-lux-hint]');
      this.spinner    = this.querySelector('[data-lux-spinner]');
      this.unitBtns   = this.querySelectorAll('[data-lux-unit]');
      // price elements
      this.priceEl        = this.querySelector('[data-lux-price]');
      this.priceCompareEl = this.querySelector('[data-lux-price-compare]');
      this.priceBadgeEl   = this.querySelector('[data-lux-price-badge]');
      // editor
      this.editor     = this.querySelector('[data-lux-editor]');
      this.editorCanvas = this.querySelector('[data-lux-editor-canvas]');
      this.zoomSlider = this.querySelector('[data-lux-zoom]');
      this.btnRemove  = this.querySelector('[data-lux-remove]');
      this.btnRotate  = this.querySelector('[data-lux-rotate]');
      this.btnClose   = this.querySelector('[data-lux-close]');
      this.btnDone    = this.querySelector('[data-lux-done]');
    }

    bindEvents() {
      var self = this;

      this.uploadBtn.addEventListener('click', function () {
        self.fileInput.click();
      });
      this.fileInput.addEventListener('change', function (e) {
        if (e.target.files && e.target.files[0]) self.handleFile(e.target.files[0]);
        e.target.value = '';
      });

      this.editBtn.addEventListener('click', function () { self.openEditor(); });
      this.buyBtn.addEventListener('click', function () { self.submitToCart('buy'); });
      if (this.atcBtn) {
        this.atcBtn.addEventListener('click', function () { self.submitToCart('cart'); });
      }

      // in / cm toggle
      this.unitBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.unit = btn.dataset.luxUnit;
          self.unitBtns.forEach(function (b) {
            b.classList.toggle('is-active', b === btn);
          });
          self.refreshSizeLabels();
        });
      });

      // editor controls
      this.zoomSlider.addEventListener('input', function () {
        self.transform.scale = parseFloat(self.zoomSlider.value);
        self.clampTransform();
        self.drawEditor();
      });
      this.btnRotate.addEventListener('click', function () {
        self.transform.rot = (self.transform.rot + 90) % 360;
        self.transform.x = 0; self.transform.y = 0;
        self.clampTransform();
        self.drawEditor();
      });
      this.btnRemove.addEventListener('click', function () { self.removePhoto(); });
      this.btnClose.addEventListener('click', function () { self.closeEditor(); });
      this.btnDone.addEventListener('click', function () {
        self.updatePhotoTexture();
        self.closeEditor();
      });

      // drag-to-pan inside editor
      this.editorDrag = { active: false, x: 0, y: 0 };
      this.editorCanvas.addEventListener('pointerdown', function (e) {
        self.editorDrag.active = true;
        self.editorDrag.x = e.clientX;
        self.editorDrag.y = e.clientY;
        self.editorCanvas.setPointerCapture(e.pointerId);
      });
      this.editorCanvas.addEventListener('pointermove', function (e) {
        if (!self.editorDrag.active) return;
        var rect = self.editorCanvas.getBoundingClientRect();
        self.transform.x += (e.clientX - self.editorDrag.x) / rect.width;
        self.transform.y += (e.clientY - self.editorDrag.y) / rect.height;
        self.editorDrag.x = e.clientX;
        self.editorDrag.y = e.clientY;
        self.clampTransform();
        self.drawEditor();
      });
      this.editorCanvas.addEventListener('pointerup', function () {
        self.editorDrag.active = false;
      });

      window.addEventListener('resize', function () { self.onResize(); });
    }

    /* ---------------- Three.js scene ---------------- */
    initThree() {
      var w = this.stage.clientWidth || 480;
      var h = this.stage.clientHeight || 560;

      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(35, w / h, 0.1, 100);
      this.camera.position.set(0, 0, 7);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      this.renderer.setSize(w, h);
      this.renderer.outputEncoding = THREE.sRGBEncoding;
      this.stage.appendChild(this.renderer.domElement);

      // flat, even lighting so the uploaded photo shows its true colours
      this.scene.add(new THREE.AmbientLight(0xffffff, 1.0));

      this.sheetGroup = new THREE.Group();
      this.sheetGroup.rotation.x = -0.05; // slight downward tilt for a nicer angle
      this.scene.add(this.sheetGroup);

      this.targetRotY = 0;
      this.targetRotX = -0.05;
      // intro "discovery" motion: tilt left -> right -> settle facing front
      this.intro = { active: true, t: 0, duration: 2600 };

      // free drag-to-rotate (with momentum) — both axes
      var self = this;
      var drag = { active: false, lastX: 0, lastY: 0, vel: 0, velY: 0 };
      var el = this.renderer.domElement;
      el.style.touchAction = 'none';
      el.style.cursor = 'grab';
      el.addEventListener('pointerdown', function (e) {
        drag.active = true;
        drag.lastX = e.clientX;
        drag.lastY = e.clientY;
        drag.vel = 0;
        drag.velY = 0;
        self.intro.active = false; // user takes over
        el.setPointerCapture(e.pointerId);
        el.style.cursor = 'grabbing';
      });
      el.addEventListener('pointermove', function (e) {
        if (!drag.active) return;
        var dx = (e.clientX - drag.lastX) * 0.01;
        var dy = (e.clientY - drag.lastY) * 0.006;
        self.targetRotY += dx;
        self.targetRotX = clamp(self.targetRotX + dy, -0.75, 0.75);
        drag.vel = dx;
        drag.velY = dy;
        drag.lastX = e.clientX;
        drag.lastY = e.clientY;
      });
      function endDrag() { drag.active = false; el.style.cursor = 'grab'; }
      el.addEventListener('pointerup', endDrag);
      el.addEventListener('pointercancel', endDrag);
      el.addEventListener('pointerleave', endDrag);
      this._drag = drag;

      this.buildSheet();
      this.animate();
    }

    /* Build / rebuild the box mesh for the current size + orientation */
    buildSheet() {
      if (this.mesh) {
        this.sheetGroup.remove(this.mesh);
        this.mesh.geometry.dispose();
        (this.mesh.material || []).forEach(function (m) {
          if (m.map) m.map.dispose();
          m.dispose();
        });
      }
      var size = this.currentSize;
      // normalise the longest edge to a fixed scene size
      var long = 3.4;
      var fw, fh;
      if (size.w >= size.h) { fw = long; fh = long * (size.h / size.w); }
      else { fh = long; fw = long * (size.w / size.h); }
      var depth = 0.06;

      var geo = new THREE.BoxGeometry(fw, fh, depth);

      // front canvas (panel + photo composited)
      this.frontCanvas = this.frontCanvas || document.createElement('canvas');
      this.frontTex = new THREE.CanvasTexture(this.frontCanvas);
      this._setupTex(this.frontTex);

      this.backTex = new THREE.CanvasTexture(this.getBackCanvas());
      this._setupTex(this.backTex);

      // plain light edges
      var edge = new THREE.MeshBasicMaterial({ color: 0xe6e6e6 });
      // front face: shows the composited photo EXACTLY as-is — no lighting,
      // no shading, no reflection. MeshBasicMaterial ignores all lights.
      var front = new THREE.MeshBasicMaterial({ map: this.frontTex });
      var back = new THREE.MeshBasicMaterial({ map: this.backTex });
      // BoxGeometry material order: +x,-x,+y,-y,+z(front),-z(back)
      this.mesh = new THREE.Mesh(geo, [edge, edge, edge, edge, front, back]);
      this.sheetGroup.add(this.mesh);

      // ---- glossy window reflection -----------------------------------
      // a separate transparent plane floating just above the print face.
      // it carries a soft white window-pane streak; because it sits on
      // top, the photo underneath is never tinted or darkened.
      if (!this._glossTex) this._glossTex = this.makeGlossTexture();
      var glossMat = new THREE.MeshBasicMaterial({
        map: this._glossTex,
        transparent: true,
        opacity: 0.55,
        depthWrite: false,
        blending: THREE.AdditiveBlending // only ever lightens, never blackens
      });
      var glossGeo = new THREE.PlaneGeometry(fw, fh);
      this.gloss = new THREE.Mesh(glossGeo, glossMat);
      this.gloss.position.z = depth / 2 + 0.002; // hover just above the face
      this.mesh.add(this.gloss);
      // -----------------------------------------------------------------

      this.loadPanels();
    }

    /* Soft white "window" reflection — bright vertical panes with a
       diagonal sweep, on a transparent canvas. Additive-blended so it
       only lightens the print, exactly like glass catching window light. */
    makeGlossTexture() {
      var c = document.createElement('canvas');
      c.width = 1024; c.height = 1024;
      var ctx = c.getContext('2d');
      ctx.clearRect(0, 0, 1024, 1024);

      ctx.save();
      ctx.translate(512, 512);
      ctx.rotate(-22 * Math.PI / 180);
      ctx.translate(-512, -512);

      function pane(x, w, peak) {
        var g = ctx.createLinearGradient(x, 0, x + w, 0);
        g.addColorStop(0.00, 'rgba(255,255,255,0)');
        g.addColorStop(0.45, 'rgba(255,255,255,' + peak + ')');
        g.addColorStop(0.55, 'rgba(255,255,255,' + peak + ')');
        g.addColorStop(1.00, 'rgba(255,255,255,0)');
        ctx.fillStyle = g;
        ctx.fillRect(x, -400, w, 1824);
      }
      pane(120, 300, 0.55);
      pane(470, 70, 0.35);
      pane(560, 150, 0.45);
      ctx.restore();

      var sheen = ctx.createLinearGradient(0, 0, 0, 1024);
      sheen.addColorStop(0.0, 'rgba(255,255,255,0.18)');
      sheen.addColorStop(0.35, 'rgba(255,255,255,0.04)');
      sheen.addColorStop(1.0, 'rgba(255,255,255,0)');
      ctx.fillStyle = sheen;
      ctx.fillRect(0, 0, 1024, 1024);

      var tex = new THREE.CanvasTexture(c);
      tex.encoding = THREE.sRGBEncoding;
      tex.generateMipmaps = false;
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.ClampToEdgeWrapping;
      return tex;
    }

    /* Sharp texture filtering — disables mipmaps so a non-power-of-two
       canvas is NOT squished to power-of-two (that squish was the blur),
       and enables anisotropic filtering for crisp angled views. */
    _setupTex(tex) {
      tex.encoding = THREE.sRGBEncoding;
      tex.generateMipmaps = false;
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.wrapS = THREE.ClampToEdgeWrapping;
      tex.wrapT = THREE.ClampToEdgeWrapping;
      if (this.renderer && this.renderer.capabilities) {
        tex.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
      }
      tex.needsUpdate = true;
    }

    getBackCanvas() {
      if (!this._backCanvas) this._backCanvas = document.createElement('canvas');
      return this._backCanvas;
    }

    /* Load the schema-defined panel images for the current orientation */
    loadPanels() {
      var self = this;
      var set = this.cfg.panels[this.currentSize.orientation] ||
                this.cfg.panels.portrait;

      this._panelImg = new Image();
      this._panelImg.crossOrigin = 'anonymous';
      this._panelImg.onload = function () { self.updatePhotoTexture(); };
      this._panelImg.onerror = function () { self.updatePhotoTexture(); };
      if (set.front) this._panelImg.src = set.front; else self.updatePhotoTexture();

      var backImg = new Image();
      backImg.crossOrigin = 'anonymous';
      backImg.onload = function () {
        var c = self.getBackCanvas();
        c.width = TEX_W; c.height = Math.round(TEX_W / self.currentSize.ratio);
        var ctx = c.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, c.width, c.height);
        self.drawCover(ctx, backImg, c.width, c.height);
        self.backTex.needsUpdate = true;
      };
      if (set.back) backImg.src = set.back;
    }

    /* Composite white panel + user photo into the front-face canvas.
       Canvas is sized to the photo's OWN resolution (capped) so the
       uploaded image is reproduced 1:1 and never upscaled/softened. */
    updatePhotoTexture() {
      var c = this.frontCanvas;
      var ratio = this.currentSize.ratio;

      var w = TEX_W;
      if (this.userImage) {
        var iw = this.userImage.naturalWidth;
        var ih = this.userImage.naturalHeight;
        var needed = Math.max(iw, ih * ratio);
        w = Math.min(TEX_W, Math.max(1024, Math.round(needed)));
      }
      c.width = w;
      c.height = Math.round(w / ratio);

      var ctx = c.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.clearRect(0, 0, c.width, c.height);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, c.width, c.height);

      if (this._panelImg && this._panelImg.complete && this._panelImg.naturalWidth) {
        this.drawCover(ctx, this._panelImg, c.width, c.height);
      }
      if (this.userImage) {
        this.drawPhoto(ctx, c.width, c.height);
      }
      if (this.frontTex) this.frontTex.needsUpdate = true;
    }

    drawCover(ctx, img, w, h) {
      var s = Math.max(w / img.naturalWidth, h / img.naturalHeight);
      var dw = img.naturalWidth * s, dh = img.naturalHeight * s;
      ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
    }

    drawPhoto(ctx, w, h) {
      var img = this.userImage;
      var rot = this.transform.rot * Math.PI / 180;
      var rw = (this.transform.rot % 180 === 0) ? img.naturalWidth : img.naturalHeight;
      var rh = (this.transform.rot % 180 === 0) ? img.naturalHeight : img.naturalWidth;
      var cover = Math.max(w / rw, h / rh);
      var eff = cover * this.transform.scale;

      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, w, h);
      ctx.clip();
      ctx.translate(w / 2 + this.transform.x * w, h / 2 + this.transform.y * h);
      ctx.rotate(rot);
      ctx.scale(eff, eff);
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
      ctx.restore();
    }

    clampTransform() {
      if (!this.userImage) return;
      var img = this.userImage;
      var ratio = this.currentSize.ratio;
      var rw = (this.transform.rot % 180 === 0) ? img.naturalWidth : img.naturalHeight;
      var rh = (this.transform.rot % 180 === 0) ? img.naturalHeight : img.naturalWidth;
      var imgRatio = rw / rh;
      var scale = this.transform.scale;
      var iwOverW = scale * Math.max(1, imgRatio / ratio);
      var ihOverH = scale * Math.max(1, ratio / imgRatio);
      var maxX = Math.max(0, (iwOverW - 1) / 2);
      var maxY = Math.max(0, (ihOverH - 1) / 2);
      this.transform.x = clamp(this.transform.x, -maxX, maxX);
      this.transform.y = clamp(this.transform.y, -maxY, maxY);
    }

    animate() {
      var self = this;
      var lastTs = performance.now();
      function loop(ts) {
        self._raf = requestAnimationFrame(loop);
        var dt = ts - lastTs;
        lastTs = ts;

        if (self.intro.active) {
          self.intro.t += dt;
          var p = Math.min(1, self.intro.t / self.intro.duration);
          var amp = 0.6;
          var angle;
          if (p < 0.42) {
            angle = -amp * Math.sin((p / 0.42) * Math.PI);
          } else if (p < 0.80) {
            var q = (p - 0.42) / 0.38;
            angle = amp * Math.sin(q * Math.PI);
          } else {
            var s = (p - 0.80) / 0.20;
            angle = (amp * 0.18) * (1 - s) * Math.cos(s * Math.PI * 0.5);
          }
          self.sheetGroup.rotation.y = angle;
          self.targetRotY = angle;
          if (p >= 1) {
            self.intro.active = false;
            self.targetRotY = 0;
          }
        } else {
          if (self._drag && !self._drag.active) {
            // drag momentum after release — both axes
            if (Math.abs(self._drag.vel) > 0.0005) {
              self.targetRotY += self._drag.vel;
              self._drag.vel *= 0.93;
            }
            if (Math.abs(self._drag.velY) > 0.0005) {
              self.targetRotX = clamp(self.targetRotX + self._drag.velY, -0.75, 0.75);
              self._drag.velY *= 0.93;
            }
          }
          var diff = self.targetRotY - self.sheetGroup.rotation.y;
          self.sheetGroup.rotation.y += diff * 0.18;
          var diffX = self.targetRotX - self.sheetGroup.rotation.x;
          self.sheetGroup.rotation.x += diffX * 0.18;
        }

        // window reflection: hidden when the print faces front
        if (self.gloss) {
          var ry = self.sheetGroup.rotation.y;
          var facing = Math.cos(ry);
          var offFront = Math.min(1, Math.abs(ry) / 0.18);
          var vis = Math.max(0, facing) * offFront;
          self.gloss.visible = vis > 0.01;
          self.gloss.material.opacity = vis * 0.6;
          self.gloss.material.map.offset.x = (ry * 0.18) % 1;
          self.gloss.material.map.needsUpdate = true;
        }

        if (self.renderer) self.renderer.render(self.scene, self.camera);
      }
      self._raf = requestAnimationFrame(loop);
    }

    onResize() {
      if (!this.renderer) return;
      var w = this.stage.clientWidth, h = this.stage.clientHeight;
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(w, h);
    }

    /* ---------------- Size / variant ----------------
       Layout: Portrait sizes on the LEFT column, Landscape on the RIGHT.
       Each orientation is labeled S / M / L by position in its group
       (1st portrait = S, 2nd = M, 3rd = L; same for landscape). */
    renderSizeButtons() {
      var self = this;
      this.sizeWrap.innerHTML = '';

      // 1. split variants into portrait / landscape, keeping their data
      var portrait = [];
      var landscape = [];
      this.variants.forEach(function (v) {
        var size = parseSize(v.title);
        var entry = { variant: v, size: size };
        if (size.orientation === 'landscape') landscape.push(entry);
        else portrait.push(entry);
      });

      // 2. interleave so they fill the 2-col grid row by row:
      //    [ portrait[0], landscape[0], portrait[1], landscape[1], ... ]
      var rows = Math.max(portrait.length, landscape.length);
      var ordered = [];
      for (var i = 0; i < rows; i++) {
        if (portrait[i])  ordered.push({ entry: portrait[i],  letter: SIZE_LETTERS[i] });
        else              ordered.push(null); // empty cell to keep alignment
        if (landscape[i]) ordered.push({ entry: landscape[i], letter: SIZE_LETTERS[i] });
        else              ordered.push(null);
      }

      // 3. render
      ordered.forEach(function (item) {
        if (!item) {
          // placeholder so the grid stays aligned when one column has fewer
          var spacer = document.createElement('span');
          spacer.className = 'lux-size-spacer';
          spacer.setAttribute('aria-hidden', 'true');
          self.sizeWrap.appendChild(spacer);
          return;
        }
        var v = item.entry.variant;
        var size = item.entry.size;
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'lux-size-btn';
        b.dataset.variantId = v.id;
        if (!v.available) b.classList.add('is-soldout');

        var letter = document.createElement('span');
        letter.className = 'lux-size-letter';
        letter.textContent = item.letter || ('#' + (item.idx || 1));

        var dim = document.createElement('span');
        dim.className = 'lux-size-dim';
        dim.dataset.luxSizeDim = '';
        dim.dataset.w = size.w;
        dim.dataset.h = size.h;
        dim.textContent = dimLabel(size, self.unit);

        b.appendChild(letter);
        b.appendChild(dim);
        b.addEventListener('click', function () {
          if (v.available) self.selectVariant(v.id);
        });
        self.sizeWrap.appendChild(b);
      });
    }

    /* re-write every size-button dimension + the SELECT SIZE value
       when the in/cm unit changes */
    refreshSizeLabels() {
      var self = this;
      this.sizeWrap.querySelectorAll('[data-lux-size-dim]').forEach(function (el) {
        el.textContent = dimLabel(
          { w: parseFloat(el.dataset.w), h: parseFloat(el.dataset.h) },
          self.unit
        );
      });
      if (this.sizeLabel && this.currentSize) {
        this.sizeLabel.textContent = dimLabel(this.currentSize, this.unit);
      }
      if (this.metaEl && this.currentSize) {
        this.metaEl.textContent = this.cfg.productType + ' • ' +
          dimLabel(this.currentSize, this.unit) + ' print';
      }
    }

    selectVariant(id, initial) {
      var v = this.variants.find(function (x) { return String(x.id) === String(id); });
      if (!v) return;
      this.currentVariant = v;
      var prevOrientation = this.currentSize && this.currentSize.orientation;
      this.currentSize = parseSize(v.title);

      this.sizeWrap.querySelectorAll('.lux-size-btn').forEach(function (b) {
        b.classList.toggle('is-active', String(b.dataset.variantId) === String(id));
      });

      // ---- price + compare-at + discount % --------------------------
      // strip any HTML the shop's money_format may emit (some stores
      // wrap prices in <span class="money">...</span>)
      function stripHTML(s) {
        return (s || '').replace(/<[^>]*>/g, '').trim();
      }
      var currentText = stripHTML(v.priceText) ||
        ('$' + (v.price / 100).toFixed(2));
      if (this.priceEl) this.priceEl.textContent = currentText;

      var hasCompare = v.compareAt && v.compareAt > v.price;
      if (this.priceCompareEl) {
        if (hasCompare) {
          this.priceCompareEl.textContent = stripHTML(v.compareAtText);
          this.priceCompareEl.hidden = false;
        } else {
          this.priceCompareEl.hidden = true;
        }
      }
      if (this.priceBadgeEl) {
        if (hasCompare) {
          var pct = Math.round((v.compareAt - v.price) / v.compareAt * 100);
          this.priceBadgeEl.textContent = pct + '%';
          this.priceBadgeEl.hidden = false;
        } else {
          this.priceBadgeEl.hidden = true;
        }
      }
      // ---------------------------------------------------------------

      this.refreshSizeLabels();

      // rebuild mesh — orientation OR ratio may have changed
      this.buildSheet();
      this.clampTransform();
    }

    /* ---------------- Upload + Cloudinary ---------------- */
    handleFile(file) {
      if (HEIC_RE.test(file.name) || file.type === 'image/heic' || file.type === 'image/heif') {
        this.showHint('iPhone HEIC photos aren\'t supported. On your iPhone go to Settings → Camera → Formats and choose "Most Compatible", or export the photo as JPEG and try again.');
        return;
      }
      if (ACCEPTED.indexOf(file.type) === -1) {
        this.showHint('Please upload a PNG, JPG or WebP image.');
        return;
      }
      if (file.size > MAX_BYTES) {
        this.showHint('Maximum file size is 20MB. Please upload a smaller image.');
        return;
      }
      this.showHint('');
      var self = this;
      this.uploading = true;
      this.updateButtonState();
      this.setUploadProgress(0);
      var reader = new FileReader();
      reader.onload = function (e) {
        var img = new Image();
        img.onload = function () {
          self.userImage = img;
          self.transform = { scale: 1, x: 0, y: 0, rot: 0 };
          self.clampTransform();
          self.updatePhotoTexture();
          self.editBtn.hidden = false;
          self.uploadBtn.classList.add('is-hidden');
          self.checkResolution();
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);

      // upload original to Cloudinary in the background so Buy Now is instant
      this.uploadToCloudinary(file);
    }

    /* Cloudinary unsigned uploads cap at 10MB. Anything at/above ~9.5MB is
       re-encoded as JPEG, stepping quality down until it fits. Quality-first:
       original pixel dimensions are preserved; only if quality alone can't
       get it under the cap do we reduce dimensions as a last resort. */
    prepareForUpload(file, done) {
      var CAP = 9.5 * 1024 * 1024;
      if (file.size < CAP) { done(file); return; }

      var reader = new FileReader();
      reader.onload = function (e) {
        var img = new Image();
        img.onload = function () {
          var qualities = [0.92, 0.85, 0.8, 0.75, 0.7, 0.6];
          var qi = 0;
          var scale = 1;

          function render() {
            var w = Math.round(img.naturalWidth * scale);
            var h = Math.round(img.naturalHeight * scale);
            var c = document.createElement('canvas');
            c.width = w; c.height = h;
            c.getContext('2d').drawImage(img, 0, 0, w, h);
            c.toBlob(function (blob) {
              if (!blob) { done(file); return; }
              if (blob.size < CAP) { done(blob); return; }
              qi++;
              if (qi < qualities.length) { render(); return; }
              /* quality exhausted — shrink dimensions and retry */
              if (scale > 0.45) {
                scale *= 0.8;
                qi = 0;
                render();
                return;
              }
              done(blob); /* best effort */
            }, 'image/jpeg', qualities[qi]);
          }
          render();
        };
        img.onerror = function () { done(file); };
        img.src = e.target.result;
      };
      reader.onerror = function () { done(file); };
      reader.readAsDataURL(file);
    }

    uploadToCloudinary(file) {
      var self = this;
      this.uploading = true;
      this.sourceUrl = null;
      this.prepareForUpload(file, function (prepared) {
        self.sendToCloudinary(prepared);
      });
    }

    sendToCloudinary(file) {
      var self = this;
      var fd = new FormData();
      fd.append('file', file);
      fd.append('upload_preset', this.cfg.uploadPreset);
      var url = 'https://api.cloudinary.com/v1_1/' + this.cfg.cloudName + '/image/upload';
      var xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);

      xhr.upload.onprogress = function (e) {
        if (!e.lengthComputable) return;
        var pct = Math.round((e.loaded / e.total) * 100);
        self.setUploadProgress(pct);
      };

      xhr.onload = function () {
        self.uploading = false;
        var data = null;
        try { data = JSON.parse(xhr.responseText); } catch (err) {}
        if (xhr.status >= 200 && xhr.status < 300 && data && data.secure_url) {
          self.sourceUrl = data.secure_url;
          self.setUploadProgress(100);
          setTimeout(function () { self.setUploadProgress(null); }, 600);
        } else {
          var msg = (data && data.error && data.error.message)
            ? data.error.message : 'no URL returned';
          console.error('[Lux] Cloudinary upload failed:', data);
          self.setUploadProgress(null);
          self.showHint('Upload failed (' + msg + '). Please try again.');
        }
        self.updateButtonState();
      };

      xhr.onerror = function () {
        self.uploading = false;
        self.setUploadProgress(null);
        self.showHint('Upload failed. Please check your connection and retry.');
        self.updateButtonState();
      };

      xhr.send(fd);
    }

    /* shows "Uploading 42%" on the stage; pass null to clear */
    setUploadProgress(pct) {
      var el = this.querySelector('[data-lux-progress]');
      if (!el) return;
      if (pct === null) { el.hidden = true; el.textContent = ''; return; }
      el.hidden = false;
      el.textContent = pct >= 100 ? 'Processing…' : 'Uploading ' + pct + '%';
    }

    /* buttons stay locked until the original is on Cloudinary */
    /* Renders the CURRENT crop to a ~1200px JPEG. Used as the cart
       thumbnail and as the print team's visual reference. */
    buildPreviewBlob(cb) {
      if (!this.userImage) { cb(null); return; }
      var ratio = this.currentSize.ratio;
      var w = 1200;
      var h = Math.round(w / ratio);
      if (h > 1200) { h = 1200; w = Math.round(h * ratio); }

      var c = document.createElement('canvas');
      c.width = w; c.height = h;
      var ctx = c.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      this.drawPhoto(ctx, w, h);
      c.toBlob(function (blob) { cb(blob); }, 'image/jpeg', 0.88);
    }

    /* one-off unsigned upload, returns the secure_url via callback */
    uploadBlob(blob, filename, cb) {
      var fd = new FormData();
      fd.append('file', blob, filename || 'luxanium-preview.jpg');
      fd.append('upload_preset', this.cfg.uploadPreset);
      fetch('https://api.cloudinary.com/v1_1/' + this.cfg.cloudName + '/image/upload', {
        method: 'POST', body: fd
      })
        .then(function (r) { return r.json(); })
        .then(function (data) { cb(data && data.secure_url ? data.secure_url : null); })
        .catch(function () { cb(null); });
    }

    /* buttons stay locked until the original is on Cloudinary */
    updateButtonState() {
      var ready = !!this.sourceUrl && !this.uploading;
      if (this.buyBtn) this.buyBtn.disabled = !ready;
      if (this.atcBtn) this.atcBtn.disabled = !ready;
    }

    checkResolution() {
      var reqW = this.currentSize.w * this.cfg.dpi;
      var reqH = this.currentSize.h * this.cfg.dpi;
      var img = this.userImage;
      this.lowRes = (img.naturalWidth < reqW || img.naturalHeight < reqH);
      this.uploadedDims = img.naturalWidth + 'x' + img.naturalHeight;
    }

    /* ---------------- Crop / zoom / rotate editor ---------------- */
    openEditor() {
      if (!this.userImage) return;
      this.editor.hidden = false;
      document.body.classList.add('lux-no-scroll');
      this.zoomSlider.value = this.transform.scale;
      this.drawEditor();
    }
    closeEditor() {
      this.editor.hidden = true;
      document.body.classList.remove('lux-no-scroll');
    }
    drawEditor() {
      var c = this.editorCanvas;
      var ratio = this.currentSize.ratio;
      var dispH = Math.min(460, window.innerHeight * 0.5);
      var dispW = Math.round(dispH * ratio);
      c.width = dispW; c.height = dispH;
      var ctx = c.getContext('2d');
      ctx.fillStyle = '#0d0d0f';
      ctx.fillRect(0, 0, dispW, dispH);
      if (this.userImage) this.drawPhoto(ctx, dispW, dispH);
    }
    removePhoto() {
      this.userImage = null;
      this.sourceUrl = null;
      this.lowRes = false;
      this.transform = { scale: 1, x: 0, y: 0, rot: 0 };
      this.editBtn.hidden = true;
      this.uploadBtn.classList.remove('is-hidden');
      this.updatePhotoTexture();
      this.closeEditor();
    }
/* ---------------- Add to Cart / Buy It Now ---------------- */
    submitToCart(mode) {
      var self = this;
      if (!this.userImage) {
        this.showHint('Please upload your photo before continuing.');
        this.uploadBtn.classList.add('lux-shake');
        setTimeout(function () { self.uploadBtn.classList.remove('lux-shake'); }, 600);
        return;
      }
      if (this.uploading) {
        this.showHint('Your image is still uploading — one moment…');
        return;
      }
      if (!this.sourceUrl) {
        this.showHint('Upload failed earlier — please remove the photo and try again.');
        return;
      }
      if (!this.currentVariant) return;

      var activeBtn = (mode === 'cart' && this.atcBtn) ? this.atcBtn : this.buyBtn;
      activeBtn.disabled = true;
      activeBtn.classList.add('is-loading');
      this.showHint('');

      /* build + upload the cropped preview, then continue */
      this.buildPreviewBlob(function (blob) {
        if (!blob) { self.finishCartSubmit(mode, activeBtn, null); return; }
        self.uploadBlob(blob, 'luxanium-preview.jpg', function (previewUrl) {
          self.finishCartSubmit(mode, activeBtn, previewUrl);
        });
      });
    }

    finishCartSubmit(mode, activeBtn, previewUrl) {
      var self = this;

      // Only the original was uploaded (on preview). The crop is
      // captured as data — the print team applies it before printing.
      var props = {
        '_Source Image': this.sourceUrl,
        '_Crop Data': JSON.stringify({
          scale: +self.transform.scale.toFixed(3),
          x: +self.transform.x.toFixed(3),
          y: +self.transform.y.toFixed(3),
          rotation: self.transform.rot
        }),
        '_Uploaded Dimensions': self.uploadedDims || '',
        '_Print Size': self.currentSize.w + 'x' + self.currentSize.h + ' in'
      };
      if (previewUrl) props['Preview Image'] = previewUrl;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: self.currentVariant.id,
          quantity: 1,
          properties: props,
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname
        })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('cart');
          return r.json();
        })
        .then(function (data) {
          if (mode === 'buy') {
            window.location.href = '/checkout';
            return;
          }
          /* Add to Cart: refresh + open the drawer */
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
            drawer.classList.remove('is-empty');
            drawer.renderContents(data);
          }
          activeBtn.disabled = false;
          activeBtn.classList.remove('is-loading');
        })
        .catch(function (err) {
          console.error('[Lux] Cart submit failed:', err);
          activeBtn.disabled = false;
          activeBtn.classList.remove('is-loading');
          self.showHint('Something went wrong. Please try again.');
        });
    }

    /* ---------------- helpers ---------------- */
    showHint(msg) {
      if (!this.hint) return;
      this.hint.textContent = msg || '';
      this.hint.hidden = !msg;
    }
    toggleSpinner(on) {
      if (this.spinner) this.spinner.hidden = !on;
    }
  }

  if (!customElements.get('lux-product-customizer')) {
    customElements.define('lux-product-customizer', LuxProductCustomizer);
  }
})();
